import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useInitDataRaw } from '@telegram-apps/sdk-react';
import axios from 'axios';
import WebGate from './components/WebGate';
import WebApp from './WebApp';

// Components
import AgeVerification from './components/AgeVerification';
import CharacterSelection from './components/CharacterSelection';
import CharacterProfile from './components/CharacterProfile';
import Chat from './components/Chat';
import Loading from './components/Loading';
// import TestComponent from './components/TestComponent';
// import SimpleTest from './components/SimpleTest';
import DebugNavigation from './components/DebugNavigation';

// API Configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://hotbump.online/api' 
  : 'http://localhost:8010/api';

const HYBRID_API_URL = process.env.NODE_ENV === 'production'
  ? 'https://hybrid.hotbump.online'
  : 'http://localhost:8001';

axios.defaults.baseURL = API_BASE_URL;

// Переводы для App
function App() {
  const initDataRaw = useInitDataRaw();
  const [user, setUser] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [characters, setCharacters] = useState({});
  const [language, setLanguage] = useState('ru');

  // Базовый путь для маршрутизатора:
  // - для Telegram Mini App, размещённого на /tg, используем basename="/tg"
  // - для всего остального оставляем по умолчанию "/"
  const baseName =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/tg')
      ? '/tg'
      : '/';

  // Автоматическое определение языка по геолокации
  const detectLanguageByGeo = async () => {
    try {
      // Проверяем сохраненный язык в localStorage
      const savedLanguage = localStorage.getItem('preferredLanguage');
      if (savedLanguage && (savedLanguage === 'ru' || savedLanguage === 'en')) {
        return savedLanguage;
      }

      // Определяем страну по IP через бесплатный API (с обработкой CORS)
      let data = null;
      try {
        const response = await fetch('https://ipapi.co/json/', {
          mode: 'cors',
          headers: {
            'Accept': 'application/json'
          }
        });
        if (response.ok) {
          data = await response.json();
        }
      } catch (fetchError) {
        // Игнорируем ошибки CORS, используем fallback
        console.warn('Geo detection failed, using browser language:', fetchError);
      }
      
      if (data && data.country_code) {
        const countryCode = data.country_code;
        
        // США -> английский, РФ -> русский, остальные -> русский по умолчанию
        if (countryCode === 'US') {
          return 'en';
        } else if (countryCode === 'RU') {
          return 'ru';
        }
      }
      
      // Для других стран используем язык браузера или русский по умолчанию
      const browserLang = navigator.language || navigator.userLanguage;
      if (browserLang.startsWith('en')) {
        return 'en';
      }
      return 'ru';
    } catch (error) {
      console.warn('Error detecting language by geo:', error);
      // В случае ошибки используем язык браузера или русский по умолчанию
      const browserLang = navigator.language || navigator.userLanguage;
      if (browserLang.startsWith('en')) {
        return 'en';
      }
      return 'ru';
    }
  };
  
  // Detect Telegram environment more reliably
  const telegramSDK = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined;
  const hasRuntimeInitData = Boolean(
    telegramSDK?.initData && telegramSDK.initData.length > 0
  );
  const hasRuntimeUser = Boolean(telegramSDK?.initDataUnsafe?.user);
  
  // useInitDataRaw returns an object only when Telegram actually passes init data.
  const hasValidInitData = Boolean(
    initDataRaw && typeof initDataRaw === 'object' && !initDataRaw.error
  );
  
  const definitelyInTelegram = Boolean(hasRuntimeInitData || hasRuntimeUser || hasValidInitData);
  const isTelegramWeb = Boolean(
    definitelyInTelegram &&
    telegramSDK?.platform &&
    telegramSDK.platform.startsWith('web')
  );
  
  useEffect(() => {
    // If we're not inside Telegram at all, no need to run the Telegram init flow.
    if (!definitelyInTelegram) {
      setLoading(false);
      return;
    }
    
    // If we're in Telegram WebApp (web version), show WebGate - skip initialization
    if (isTelegramWeb) return;
    
    // If we're on a /web/* route, let the web app handle it
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    if (currentPath.startsWith('/web')) {
      return;
    }
    
    const initializeApp = async () => {
      try {
        // Определяем язык по геолокации при первой загрузке
        const detectedLanguage = await detectLanguageByGeo();
        setLanguage(detectedLanguage);
        
        // Проверяем, есть ли ошибка с initDataRaw
        if (initDataRaw && initDataRaw.error) {
          console.log('initDataRaw error detected, using test mode');
          const testInitData = "test";
          setUser({
            initData: testInitData
          });
          
          await loadCharactersInfo(detectedLanguage);
          
          try {
            const response = await axios.get(`/user_status?initData=${encodeURIComponent(testInitData)}`);
            console.log('User status response:', response.data);
            setUserStatus(response.data);
          } catch (error) {
            console.log('User status not found, starting fresh');
            setUserStatus({
              age_verified: false,
              daily_messages: 0,
              daily_limit: 50
            });
          }
        } else if (initDataRaw && !initDataRaw.error) {
          // Устанавливаем пользователя
          console.log('initDataRaw:', initDataRaw);
          setUser({
            initData: typeof initDataRaw === 'string' ? initDataRaw : JSON.stringify(initDataRaw)
          });

          // Получаем информацию о персонажах и их ботах
          await loadCharactersInfo(detectedLanguage);

          // Проверяем статус пользователя
          try {
            const response = await axios.get(`/user_status?initData=${encodeURIComponent(typeof initDataRaw === 'string' ? initDataRaw : JSON.stringify(initDataRaw))}`);
            setUserStatus(response.data);
          } catch (error) {
            console.log('User status not found, starting fresh');
            setUserStatus({
              age_verified: false,
              daily_messages: 0,
              daily_limit: 50
            });
          }
        } else {
          // Для тестирования без Telegram
          console.log('No initDataRaw, using test mode');
          const testInitData = "test";
          setUser({
            initData: testInitData
          });
          
          await loadCharactersInfo(detectedLanguage);
          
          try {
            const response = await axios.get(`/user_status?initData=${encodeURIComponent(testInitData)}`);
            console.log('User status response:', response.data);
            setUserStatus(response.data);
          } catch (error) {
            console.log('User status not found, starting fresh');
            setUserStatus({
              age_verified: false,
              daily_messages: 0,
              daily_limit: 50
            });
          }
        }
      } catch (error) {
        console.error('App initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, [initDataRaw, isTelegramWeb, definitelyInTelegram]);

  // Загружаем информацию о персонажах и их ботах
  const loadCharactersInfo = async (language = 'ru') => {
    try {
      // Сначала пробуем обычный API (более надежный)
      const response = await axios.get(`/characters?language=${language}`);
      setCharacters(response.data.characters || {});
    } catch (error) {
      console.error('Failed to load characters from main API:', error);
      // Fallback к hybrid API только если основной не работает
      try {
        const response = await axios.get(`${HYBRID_API_URL}/api/characters?language=${language}`);
        setCharacters(response.data.characters || {});
      } catch (fallbackError) {
        console.error('Failed to load characters from hybrid API:', fallbackError);
        // Устанавливаем пустой объект персонажей
        setCharacters({});
      }
    }
  };

  // Обновляем статус пользователя
  const updateUserStatus = async () => {
    if (!user?.initData) return;
    
    console.log('Updating user status...');
    try {
      const response = await axios.get(`/user_status?initData=${encodeURIComponent(user.initData)}`);
      console.log('User status updated:', response.data);
      setUserStatus(response.data);
    } catch (error) {
      console.error('Failed to update user status:', error);
    }
  };

  // Обновляем язык и перезагружаем персонажей
  const updateLanguage = async (newLanguage) => {
    setLanguage(newLanguage);
    // Сохраняем выбранный язык в localStorage
    localStorage.setItem('preferredLanguage', newLanguage);
    await loadCharactersInfo(newLanguage);
  };

  // Создаем переход к персонажному боту
  const createBotTransition = async (characterId) => {
    if (!user?.initData) return null;

    try {
      const userId = extractUserId(user.initData);
      if (!userId) return null;

      const transitionData = {
        user_id: userId,
        character_id: characterId,
        from_bot: "mini_app",
        to_bot: `${characterId}_bot`,
        context: {
          timestamp: new Date().toISOString(),
          source: "mini_app"
        }
      };

      const response = await axios.post(`${HYBRID_API_URL}/api/transition`, transitionData);
      return response.data;
    } catch (error) {
      console.error('Failed to create bot transition:', error);
      return null;
    }
  };

  // Извлекаем user_id из initData
  const extractUserId = (initData) => {
    try {
      const data = typeof initData === 'string' ? JSON.parse(initData) : initData;
      if (data.result?.initData?.user?.id) {
        return data.result.initData.user.id;
      }
      return null;
    } catch (error) {
      console.error('Error extracting user ID:', error);
      return null;
    }
  };
  
  
  // We're definitely inside Telegram. If it's Telegram Web, nudge the user.
  if (isTelegramWeb) {
    return <WebGate />;
  }

  // For Telegram Mini App, continue with normal flow
  if (loading) {
    return <Loading />;
  }

  // For Telegram Mini App, user might not be initialized yet, but that's OK
  // We'll handle it in the routes

  // Отладочная информация
  console.log('App render - userStatus:', userStatus);
  console.log('App render - age_verified:', userStatus?.age_verified);
  console.log('App render - characters:', characters);

  return (
    <Router basename={baseName}>
      <div className="min-h-screen bg-dark-900">
        <Routes>
          {/* Web App routes - handled by WebApp component (only for /web/* paths, NOT in Telegram) */}
          <Route path="/web/*" element={<WebApp />} />
          
          {/* Root redirect - depends on context */}
          <Route 
            path="/" 
            element={
              // If we're in Telegram (check both isInTelegram and initDataRaw), use Telegram Mini App flow
              definitelyInTelegram ? (
                userStatus?.age_verified ? (
                  <Navigate to="/characters" replace />
                ) : (
                  <Navigate to="/age-verification" replace />
                )
              ) : (
                // If we're NOT in Telegram, redirect to web app
                <Navigate to="/web/explore" replace />
              )
            } 
          />

          {/* Проверка возраста - ОБЯЗАТЕЛЬНАЯ первая страница для Telegram Mini App */}
          <Route 
            path="/age-verification" 
            element={
              // Only show for Telegram Mini App users (check both isInTelegram and initDataRaw)
              definitelyInTelegram ? (
                <AgeVerification 
                  user={user} 
                  userStatus={userStatus}
                  onVerified={updateUserStatus}
                  language={language}
                  onLanguageChange={updateLanguage}
                />
              ) : (
                // Not in Telegram - redirect to web app
                <Navigate to="/web/explore" replace />
              )
            } 
          />

          {/* Выбор персонажа - только после проверки возраста (для Telegram Mini App) */}
          <Route 
            path="/characters" 
            element={
              // Only show for Telegram Mini App users (check both isInTelegram and initDataRaw)
              definitelyInTelegram ? (
                userStatus?.age_verified ? (
                  <CharacterSelection 
                    user={user} 
                    userStatus={userStatus}
                    characters={characters}
                    onCharacterSelected={updateUserStatus}
                    createBotTransition={createBotTransition}
                    language={language}
                    onLanguageChange={updateLanguage}
                  />
                ) : (
                  <Navigate to="/age-verification" replace />
                )
              ) : (
                // Not in Telegram - redirect to web app
                <Navigate to="/web/explore" replace />
              )
            } 
          />

          {/* Индивидуальная страница персонажа (для Telegram Mini App) */}
          <Route 
            path="/characters/:slug" 
            element={
              definitelyInTelegram ? (
                <CharacterProfile language={language} onLanguageChange={updateLanguage} />
              ) : (
                <Navigate to="/web/explore" replace />
              )
            } 
          />

          {/* Чат с персонажем - теперь редирект к боту (для Telegram Mini App) */}
          <Route 
            path="/chat/:characterId" 
            element={
              // Only show for Telegram Mini App users (check both isInTelegram and initDataRaw)
              definitelyInTelegram ? (
                userStatus?.age_verified ? (
                  <Chat 
                    user={user} 
                    userStatus={userStatus}
                    characters={characters}
                    createBotTransition={createBotTransition}
                  />
                ) : (
                  <Navigate to="/age-verification" replace />
                )
              ) : (
                // Not in Telegram - redirect to web app
                <Navigate to="/web/explore" replace />
              )
            } 
          />

          {/* Отладка навигации */}
          <Route 
            path="/debug" 
            element={<DebugNavigation />}
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
